import api from '../utils/api';
import config from '../config';
import {
  DEVICE_STATUS_APPROVED,
  DEVICE_STATUS_REJECTED,
  DEVICE_STATUS_REVOKED,
} from '../constants';

const deviceService = {
  // Get init data (statuses, etc.)
  getInit: async () => {
    return api.get(config.endpoints.devices.init);
  },

  // Get devices list with pagination
  getDevices: async (params = {}) => {
    // Filter out empty values and convert string numbers to integers
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        // Convert string numbers to integers for unitId and status
        if ((key === 'unitId' || key === 'status') && typeof params[key] === 'string') {
          cleanParams[key] = parseInt(params[key], 10);
        } else {
          cleanParams[key] = params[key];
        }
      }
    });

    console.log('🔍 DeviceService getDevices - Original params:', params);
    console.log('🔍 DeviceService getDevices - Clean params:', cleanParams);

    return api.get(
      config.endpoints.devices.list,
      cleanParams,
      {
        paramsSerializer: {
          indexes: null, // Ensure simple query string without nested indexes
        },
      }
    );
  },

  // Get device by ID
  getDeviceById: async (id) => {
    return api.get(`${config.endpoints.devices.getById}/${id}`);
  },

  // Create new device
  createDevice: async (deviceData) => {
    return api.post(config.endpoints.devices.create, deviceData);
  },

  // Update device
  updateDevice: async (deviceData) => {
    return api.put(`${config.endpoints.devices.update}/${deviceData.id}`, deviceData);
  },

  // Delete device
  deleteDevice: async (id) => {
    return api.delete(`${config.endpoints.devices.delete}/${id}`);
  },

  // Bulk delete devices
  bulkDeleteDevices: async (ids) => {
    return api.delete(config.endpoints.devices.delete, {
      data: ids, // Send IDs in request body as JSON array
    });
  },

  // Review actions - use specific endpoints for each action
  approveDevice: async (id) => {
    return api.post(`${config.endpoints.devices.list}/${id}/approve`);
  },

  rejectDevice: async (id, statusNote) => {
    return api.post(`${config.endpoints.devices.list}/${id}/reject`, { statusNote });
  },

  revokeDevice: async (id, statusNote) => {
    return api.post(`${config.endpoints.devices.list}/${id}/revoke`, { statusNote });
  },

  // Toggle LGSP Access
  toggleLGSPAccess: async (id, allowLGSPAccess) => {
    return api.put(`${config.endpoints.devices.list}/${id}/lgsp-access`, { allowLGSPAccess });
  },

  // Update Connection Status
  updateConnectionStatus: async (id, connectionStatus, reason) => {
    return api.patch(`${config.endpoints.devices.list}/${id}/connection-status`, { id, connectionStatus, reason });
  },

  // Verify Citizen using device authentication
  verifyCitizen: async (request) => {
    return api.post(`${config.endpoints.devices.list}/verify-citizen`, request);
  },

  // Bulk re-approve devices
  bulkReapproveDevices: async (ids, statusNote) => {
    return api.post(`${config.endpoints.devices.list}/bulk-reapprove`, { ids, statusNote });
  },

  // Assign unit to device
  assignUnit: async ({ id, unitId }) => {
    return api.post(`${config.endpoints.devices.list}/${id}/assign-unit`, { DeviceId: id, UnitId: unitId });
  },

  // Import devices from Excel file
  importDevices: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(config.endpoints.devices.import, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Export devices to Excel
  exportDevicesToExcel: async (params = {}, selectedIds = null) => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        // Convert string numbers to integers for unitId and status
        const value = (key === 'unitId' || key === 'status') && typeof params[key] === 'string'
          ? parseInt(params[key], 10)
          : params[key];
        queryParams.append(key, value);
      }
    });

    const url = `${config.api.baseURL}${config.endpoints.devices.exportExcel}?${queryParams.toString()}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const requestOptions = {
      method: selectedIds && selectedIds.length > 0 ? 'POST' : 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(selectedIds && selectedIds.length > 0 && { 'Content-Type': 'application/json' }),
      },
      ...(selectedIds && selectedIds.length > 0 && { body: JSON.stringify(selectedIds) }),
    };

    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `Devices_${new Date().toISOString().slice(0,10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Export devices to PDF
  exportDevicesToPdf: async (params = {}, selectedIds = null) => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        // Convert string numbers to integers for unitId and status
        const value = (key === 'unitId' || key === 'status') && typeof params[key] === 'string'
          ? parseInt(params[key], 10)
          : params[key];
        queryParams.append(key, value);
      }
    });

    const url = `${config.api.baseURL}${config.endpoints.devices.exportPdf}?${queryParams.toString()}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const requestOptions = {
      method: selectedIds && selectedIds.length > 0 ? 'POST' : 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(selectedIds && selectedIds.length > 0 && { 'Content-Type': 'application/json' }),
      },
      ...(selectedIds && selectedIds.length > 0 && { body: JSON.stringify(selectedIds) }),
    };

    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `Devices_${new Date().toISOString().slice(0,10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Download import template
  downloadTemplate: async () => {
    const url = `${config.api.baseURL}${config.endpoints.devices.importTemplate}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('devices.errors.downloadTemplateFailed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'Device_Import_Template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
};

export default deviceService;
